import axios from "axios";
import { getAuthHeader } from "./authHeader";
import { VideoProbe } from "./validateVideoProbe";

/**
 * Call POST /services/probe-video for a single uploaded file.
 *
 * The endpoint mints a read-signed URL on the server, forwards to marabot-video's
 * /video-probe, and returns raw ffprobe data. Use `validateVideoProbe` against a
 * policy to decide whether the file may proceed.
 */
export const probeUploadedVideo = async (
    filename: string,
    origName: string,
    type: "asset" | "listing"
): Promise<VideoProbe> => {
    const authHeader = await getAuthHeader();
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

    const { data } = await axios.post<VideoProbe[]>(
        `${backendUrl}/services/probe-video`,
        { files: [filename], names: [origName], type },
        { headers: authHeader }
    );

    if (!Array.isArray(data) || data.length === 0) {
        throw new Error("Probe service returned no result");
    }
    return data[0];
};

/**
 * Best-effort delete for an orphan uploaded GCS object — a file uploaded via
 * signed URL but never persisted as an Asset.
 *
 * Used to clean up after a failed probe. Swallows errors: the cleanup must not
 * block the user-facing error flow, and the server logs the failure.
 */
export const removeUploadedFile = async (
    filename: string,
    type: "asset" | "listing"
): Promise<void> => {
    try {
        const authHeader = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        await axios.delete(`${backendUrl}/assets/uploaded-file`, {
            headers: authHeader,
            data: { filename, type },
        });
    } catch {
        // Cleanup is best-effort. Server logs the failure; UI continues.
    }
};
